import { GlobalSettings, useGlobalSettings } from '../../stores';
import { defaultTtsVoice, Design, DesignChannelType, DesignLanguage, LanguageCode } from '../../types';
import { PromptAudioController } from './prompt-audio-controller';
import { ChannelSelector, TtsVoiceSelector } from '../../form_features';
import { PromptNodeModel } from '../../models';
import PromptTextarea from '../PromptTextarea';
import { getLanguageLabel, getPromptText } from '../../utilities';
import styles from './style/PromptTextAreas.module.scss';

export type PromptTextAreaProps = {
  context: any;
  setConfig: PromptNodeModel['setConfig'];
  update: PromptNodeModel['update'];
};

export type LanguageGroups = Record<
  LanguageCode,
  Partial<DesignLanguage> & {
    voices?: DesignLanguage['voice'][];
    ttsFallBackVoices?: DesignLanguage['ttsFallBackVoice'][];
  }
>;

export type UniqueLanguages = (DesignLanguage & {
  count: number;
})[];

export type GetPromptText = ({
  channel,
  context,
  languageCode,
  serialized
}: {
  channel: DesignChannelType | null;
  context: PromptNodeModel;
  languageCode: LanguageCode;
  serialized: Design;
  ttsVoice?: GlobalSettings['ttsVoices'];
}) => string;

export function PromptTextAreas({ context: node, update }: PromptTextAreaProps) {
  const { serialized } = window.apm;
  const currentCachedModule = window.apm.getCurrentDiagramWidget().props.node;
  const currentModule = serialized.components.modules.find((m) => m.id === currentCachedModule.id);
  const { channel, ttsVoices } = useGlobalSettings();
  const { isOmniChannel } = serialized.omniChannel;

  let usedLanguages = [] as LanguageCode[];

  const languages = serialized.languages.reduce((acc, language) => {
    if (usedLanguages.includes(language.language)) {
      return acc;
    }
    usedLanguages.push(language.language);
    return [
      ...acc,
      {
        ...language,
        count: serialized.languages.filter(({ language: languageCode }) => languageCode === language.language).length
      }
    ];
  }, [] as UniqueLanguages);

  return (
    <>
      {languages.map((language) => {
        const { count, language: languageCode, ttsFallBackVoice, voice } = language;
        const multipleTtsPerLanguage = count > 1;
        const ttsVoice = (multipleTtsPerLanguage) ? ttsVoices[languageCode] ?? defaultTtsVoice : ttsFallBackVoice;
        const disableAudioGen = multipleTtsPerLanguage && ttsVoice === defaultTtsVoice;

        return [
          <div
            className={styles.labelWrapper}
            key={languageCode + '-label'}
          >
            <label>
              {getLanguageLabel({
                designLanguage: language,
                includeTts: !multipleTtsPerLanguage
              })}
            </label>
            {(multipleTtsPerLanguage) ? (
              <div
                className={styles.optionsWrapper}
                data-options
              >
                <small>TTS Voice:</small>{' '}
                <TtsVoiceSelector
                  languageCode={languageCode}
                  onChange={update}
                />
              </div>
            ) : (
              null
            )}
          </div>,
          <PromptTextarea
            key={languageCode + '-textarea'}
            {...{
              context: node,
              hideLabel: true,
              isPropertyPanel: true,
              lang: languageCode,
              valKey: 'text',
              value: { [languageCode]: getPromptText({ languageCode, node }) }
            }}
          />,
          ...(channel !== DesignChannelType.voice
            ? []
            : [
              <PromptAudioController
                disabled={disableAudioGen}
                key={languageCode + '-audio'}
                language={{
                  language: languageCode!,
                  voice,
                  ttsFallBackVoice: ttsVoice === defaultTtsVoice ? ttsFallBackVoice : ttsVoice
                }}
                node={node}
                nodeModule={currentModule!}
                tooltip={
                  disableAudioGen ? (
                    <span>
                      <u>Select a TTS Voice</u> to generate audio with.
                    </span>
                  ) : undefined
                }
                ttsVoice={ttsVoice}
              />
            ])
        ];
      })}
      {!isOmniChannel ? null : (
        <div className="form-component-container">
          <label>Channel Layer</label>
          <ChannelSelector onChange={update} />
        </div>
      )}
    </>
  );
}
